import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@material-ui/core';
import { GOOGLE_MAPS_API_KEY, GOOGLE_PLACES_AUTOCOMPLETE_URL, GOOGLE_PLACES_DETAILS_URL } from '../config/googleApiConfig';

const AddressAutocomplete = ({ 
    label, 
    value, 
    onChange, 
    onPlaceSelected,
    error, 
    helperText,
    style,
    placeholder = "Start typing your address...",
    disabled = false
}) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [predictions, setPredictions] = useState([]);
    const [showPredictions, setShowPredictions] = useState(false);
    const searchInputRef = useRef(null);
    const predictionsRef = useRef(null);

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        // 点击外部关闭预测下拉框
        const handleClickOutside = (event) => {
            if (
                searchInputRef.current && !searchInputRef.current.contains(event.target) &&
                predictionsRef.current && !predictionsRef.current.contains(event.target)
            ) {
                setShowPredictions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 处理输入变化
    const handleInputChange = async (event) => {
        const inputVal = event.target.value;
        setInputValue(inputVal);

        // 触发父组件的 onChange
        if (onChange) {
            onChange({ target: { value: inputVal } });
        }

        // 当用户开始输入时，调用 Google Places API 获取预测
        if (inputVal.length > 2) {
            try {
                const response = await fetch(GOOGLE_PLACES_AUTOCOMPLETE_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY
                    },
                    body: JSON.stringify({
                        input: inputVal,
                        includedPrimaryTypes: ['street_address', 'premise', 'subpremise']
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.suggestions && data.suggestions.length > 0) {
                        setPredictions(data.suggestions);
                        setShowPredictions(true);
                    } else {
                        setPredictions([]);
                        setShowPredictions(false);
                    }
                } else {
                    console.error('Autocomplete API error:', response.status);
                    setPredictions([]);
                    setShowPredictions(false);
                }
            } catch (error) {
                console.error('Error fetching autocomplete predictions:', error);
                setPredictions([]);
                setShowPredictions(false);
            }
        } else {
            setPredictions([]);
            setShowPredictions(false);
        }
    };

    // 处理选择预测项
    const handleSelectPrediction = async (suggestion) => {
        const placeId = suggestion.placePrediction && suggestion.placePrediction.placeId;
        const description = (suggestion.placePrediction && suggestion.placePrediction.text && suggestion.placePrediction.text.text) || '';
        
        setInputValue(description);
        setShowPredictions(false);

        // 触发父组件的 onChange
        if (onChange) {
            onChange({ target: { value: description } });
        }

        // 获取完整的地址详情
        if (placeId) {
            try {
                const response = await fetch(
                    `${GOOGLE_PLACES_DETAILS_URL}/${placeId}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
                            'X-Goog-FieldMask': 'addressComponents,formattedAddress'
                        }
                    }
                );

                if (response.ok) {
                    const place = await response.json();
                    
                    // 解析地址组件
                    const addressComponents = {};
                    if (place.addressComponents) {
                        place.addressComponents.forEach(component => {
                            component.types.forEach(type => {
                                addressComponents[type] = component.longText || component.shortText;
                            });
                        });
                    }

                    // 构建地址对象并返回给父组件
                    const addressData = {
                        address1: addressComponents['street_number'] && addressComponents['route'] ? `${addressComponents['street_number']} ${addressComponents['route']}` : description.split(",")[0],
                        city: addressComponents['locality'] || '',
                        state: addressComponents['administrative_area_level_1'] || '',
                        postalCode: addressComponents['postal_code'] || '',
                        country: addressComponents['country'] || ''
                    };

                    if (onPlaceSelected) {
                        onPlaceSelected(addressData);
                    }
                } else {
                    console.error('Place Details API error:', response.status);
                }
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        }
    };

    return (
        <div style={{ position: 'relative', ...style }}>
            <TextField
                inputRef={searchInputRef}
                label={label}
                value={inputValue}
                onChange={handleInputChange}
                variant="outlined"
                fullWidth
                error={error}
                helperText={helperText}
                placeholder={placeholder}
                disabled={disabled}
            />
            
            {showPredictions && predictions.length > 0 && (
                <div
                    ref={predictionsRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        marginTop: '4px'
                    }}
                >
                    {predictions.map((suggestion, index) => {
                        const mainText = (suggestion.placePrediction && suggestion.placePrediction.structuredFormat && suggestion.placePrediction.structuredFormat.mainText && suggestion.placePrediction.structuredFormat.mainText.text) || '';
                        const secondaryText = (suggestion.placePrediction && suggestion.placePrediction.structuredFormat && suggestion.placePrediction.structuredFormat.secondaryText && suggestion.placePrediction.structuredFormat.secondaryText.text) || '';
                        const placeId = (suggestion.placePrediction && suggestion.placePrediction.placeId) || '';
                        
                        return (
                            <div
                                key={placeId || index}
                                onClick={() => handleSelectPrediction(suggestion)}
                                style={{
                                    padding: '10px 15px',
                                    cursor: 'pointer',
                                    borderBottom: index < predictions.length - 1 ? '1px solid #f0f0f0' : 'none'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                            >
                                <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{mainText}</div>
                                <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
                                    {secondaryText}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default AddressAutocomplete;
